import { Button } from '@slal/ui/buttons';
import { PencilIcon } from '@slal/ui/icons';
import { Container } from '@slal/ui/layout';
import { FormProvider, yupResolver } from '@slal/ui/react-hook-form/core';
import { ComboBoxField, Field, FieldSet, TextField } from '@slal/ui/react-hook-form/fields';
import { normalizeNoSpaces, normalizePhoneNumber } from '@slal/ui/react-hook-form/utils';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router';
import { DateField, MaxWidthContent, StyledForm } from '~/components';
import { useApplicationContext, useApplicationForm, useNormalizations } from '~/hooks';
import { completePersonalInformationSchema, personalInformationValidationSchema } from '~/schemas';
import { ApplicationFormKeys, CompletePersonalInformationSchema, PersonalInformationSchema } from '~/types';
import { AdditionalDetailsFieldSet } from '../AdditionalDetailsForm';
import { ApplicationButtons } from '../ApplicationButtons';
import { StyledModal, StyledModalBody, StyledModalFooter } from './styles';
import { titleSelectOptions } from './utils';

export const PersonalInformationFieldSet = () => {
    const { normalizePhoneNumber: normalizePhone, normalizeNoSpaces: normalizeEmail } = useNormalizations({
        normalizePhoneNumber,
        normalizeNoSpaces,
    });
    return (
        <FieldSet aria-label="Personal information">
            <Field label="Title" labelFor="personalInformation.title">
                <ComboBoxField
                    name="personalInformation.title"
                    id="personalInformation.title"
                    options={titleSelectOptions}
                />
            </Field>

            <Field label="First name" labelFor="personalInformation.firstName">
                <TextField name="personalInformation.firstName" id="personalInformation.firstName" maxLength={35} />
            </Field>

            <Field label="Last name" labelFor="personalInformation.lastName">
                <TextField name="personalInformation.lastName" id="personalInformation.lastName" maxLength={35} />
            </Field>

            <Field label="Date of birth" errorFor="personalInformation.dateOfBirth">
                <DateField name="personalInformation.dateOfBirth" />
            </Field>

            <Field label="Email address" labelFor="personalInformation.emailAddress">
                <TextField
                    name="personalInformation.emailAddress"
                    id="personalInformation.emailAddress"
                    setValueAs={normalizeEmail}
                />
            </Field>

            <Field label="Phone number" labelFor="personalInformation.phoneNumber">
                <TextField
                    name="personalInformation.phoneNumber"
                    id="personalInformation.phoneNumber"
                    setValueAs={normalizePhone}
                    minLength={11}
                    maxLength={15}
                />
            </Field>
        </FieldSet>
    );
};
export const PersonalInformationForm = () => {
    const { handleSubmit, form } = useApplicationForm<PersonalInformationSchema>(
        {
            resolver: yupResolver(personalInformationValidationSchema),
        },
        ApplicationFormKeys.PERSONAL_INFORMATION
    );
    const navigate = useNavigate();
    const onValidCallback = () => navigate('/application/additional-details');

    return (
        <MaxWidthContent>
            <FormProvider {...form}>
                <StyledForm onSubmit={handleSubmit(onValidCallback)}>
                    <PersonalInformationFieldSet />
                    <ApplicationButtons backTo="/application/advice" />
                </StyledForm>
            </FormProvider>
        </MaxWidthContent>
    );
};

export const EditPersonalInformationFormModal = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const closeModal = useCallback(() => setIsModalOpen(false), [setIsModalOpen]);
    const openModal = useCallback(() => setIsModalOpen(true), [setIsModalOpen]);
    const { refreshApplication } = useApplicationContext();

    const { handleSubmit, form } = useApplicationForm<CompletePersonalInformationSchema>(
        {
            resolver: yupResolver(completePersonalInformationSchema),
        },
        ApplicationFormKeys.PERSONAL_INFORMATION
    );

    const onValidCallback = useCallback(() => {
        refreshApplication();
        closeModal();
    }, [closeModal, refreshApplication]);

    return (
        <>
            <Button startIcon={<PencilIcon />} color="secondary" variant="outline" onClick={openModal}>
                Change
            </Button>
            <StyledModal isOpen={isModalOpen} onClose={closeModal} title="Update your details">
                <Container>
                    <FormProvider {...form}>
                        <StyledForm onSubmit={handleSubmit(onValidCallback)}>
                            <StyledModalBody>
                                <PersonalInformationFieldSet />
                                <AdditionalDetailsFieldSet />
                            </StyledModalBody>
                            <StyledModalFooter>
                                <Button type="submit" color="secondary">
                                    Update
                                </Button>
                                <Button onClick={closeModal} variant="outline">
                                    Cancel
                                </Button>
                            </StyledModalFooter>
                        </StyledForm>
                    </FormProvider>
                </Container>
            </StyledModal>
        </>
    );
};
